import { toastController, type ToastButton } from "@ionic/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import lodashIsNumber from "lodash/fp/isNumber";
import {
  app_platform_type,
  notification_event_type,
} from "prisma-generated/enums";
import React, { useEffect } from "react";
import { apiRequest } from "shared/api/apiRequest";
import { postSocietyProfileNotificationsSubscriptionContract } from "shared/api/contracts/society/societyId/profiles/profileId/notifications/subscription";
import { getSocietyProfileNotificationsUnreadCountContract } from "shared/api/contracts/society/societyId/profiles/profileId/notifications/unreadCount";
import type { UnreadNotificationCountResponse } from "shared/api/types/society/[societyId]/profiles/[profileId]/notifications";
import { usePillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { findRoute } from "shared/helpers/routes/findRoute";
import { useWebSocket } from "shared/hooks/useWebSocket";
import type {
  MessageNotificationMetadata,
  WebsocketNotificationEvent,
} from "shared/types/notification-event";
import {
  NotificationsContext,
  type NotificationsContextType,
} from "hub/src/context/NotificationsProvider/NotificationsContext";
import {
  isChatMessage,
  isNotificationMessage,
  type MessageNotificationWebsocketEvent,
  type PushNotificationWebsocketEvent,
} from "hub/src/context/NotificationsProvider/utils";
import { useDeviceData } from "hub/src/hooks/useDeviceData";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";
import { usePushNotifications } from "hub/src/hooks/usePushNotifications";

type FCMNotificationData = {
  notificationEventId: string;
  action: notification_event_type;
  platform: app_platform_type;
  messageNotificationMetadata?: string;
};

const TEN_SECONDS_IN_MILLISECONDS = 10 * 1000;

export const NotificationsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const router = useCustomRouter();
  const session = useCustomSession();

  const device = useDeviceInfo();
  const deviceData = useDeviceData();

  const notificationsModalControl = usePillarModalControl();

  const chatDetailsRouteConfig = findRoute(session?.society?.routes ?? [], {
    componentName: "chatDetailsPage",
  });

  const {
    pushNotificationsEnabled = false,
    websocketEnabled = false,
    websocketEndpointBaseUrl = "",
  } = session?.society?.societySettingsPublic || {};

  const disabled = !session?.societyId || !session?.profileId || !deviceData;
  const pushDisabled =
    disabled || !pushNotificationsEnabled || !device.isNativeApp;
  const socketDisabled =
    disabled || !websocketEnabled || !websocketEndpointBaseUrl;
  const notificationsDisabled = disabled || !device.isNativeApp;

  const unreadNotificationsQuery = useQuery<UnreadNotificationCountResponse>({
    queryKey: [
      "notificationsUnreadCount",
      session?.societyId?.toString() ?? "",
      session?.profileId?.toString() ?? "",
    ],
    queryFn: async () => {
      if (!session?.societyId || !session?.profileId) {
        return {
          unreadCount: 0,
        };
      }

      return apiRequest({
        contract: getSocietyProfileNotificationsUnreadCountContract,
        inputs: {
          params: {
            societyId: session.societyId,
            profileId: session.profileId,
          },
        },
      });
    },
    enabled: !notificationsDisabled,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  });

  const invalidateNotificationsQuery = async () => {
    if (!session?.societyId || !session?.profileId) {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: [
        "notificationsUnreadCount",
        session.societyId.toString(),
        session.profileId.toString(),
      ],
    });
    await queryClient.invalidateQueries({
      queryKey: [
        "pillarTable",
        "notifications",
        session.societyId.toString(),
        session.profileId.toString(),
      ],
    });
  };

  const invalidateChatsQuery = async (chatId: number) => {
    if (!session?.societyId || !session?.profileId) {
      return;
    }

    await queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        return (
          queryKey.includes("chats") ||
          queryKey.includes(`chat-${chatId}`) ||
          queryKey.includes(`chat-${chatId}-messages`)
        );
      },
    });
  };

  const handlePushNotificationMessage = async (
    message: PushNotificationWebsocketEvent,
  ) => {
    // Invalidate queries
    await invalidateNotificationsQuery();

    // Don't show the push_notification toast if push notifications are disabled
    if (notificationsDisabled) {
      return;
    }

    // Toast configuration
    const buttons: ToastButton[] = [
      {
        text: "Cancel",
        role: "cancel",
      },
      {
        text: "Open",
        handler: () => {
          notificationsModalControl.setIsOpen(true);
        },
      },
    ];

    const toast = await toastController.create({
      header: message?.notification?.title,
      message: message?.notification?.body,
      buttons: buttons,
      position: "top",
      color: "secondary",
      layout: "stacked",
      swipeGesture: "vertical",
      cssClass: "notification-toast",
      duration: TEN_SECONDS_IN_MILLISECONDS,
    });

    await toast.present();
  };

  const handleMessageNotificationMessage = async (
    message: MessageNotificationWebsocketEvent,
  ) => {
    const chatDetailsRouteUrl = chatDetailsRouteConfig?.url;
    const chatId = message.messageNotificationMetadata.chatId;

    // Invalidate queries
    await invalidateNotificationsQuery();
    await invalidateChatsQuery(message.messageNotificationMetadata.chatId);

    // Don't show the message_notification toast if chat notifications are disabled
    // chatMessagesDisabled = disabled
    if (disabled) {
      return;
    }

    // Toast configuration
    const buttons: ToastButton[] = [
      {
        text: "Cancel",
        role: "cancel",
      },
    ];

    if (chatDetailsRouteUrl) {
      buttons.push({
        text: "Open",
        handler: () => {
          router.removeFromState("profile");
          router.push(chatDetailsRouteUrl, { chatId });
        },
      });
    }

    const toast = await toastController.create({
      header: message?.notification?.title,
      message: message?.notification?.body,
      buttons: buttons,
      position: "top",
      color: "secondary",
      layout: "stacked",
      swipeGesture: "vertical",
      cssClass: "notification-toast",
      duration: TEN_SECONDS_IN_MILLISECONDS,
    });

    await toast.present();
  };

  usePushNotifications({
    disabled: pushDisabled,
    listeners: {
      registration: async (token) => {
        if (!session?.societyId || !session?.profileId) {
          return;
        }

        await apiRequest({
          contract: postSocietyProfileNotificationsSubscriptionContract,
          inputs: {
            params: {
              societyId: session.societyId,
              profileId: session.profileId,
            },
            body: {
              deviceIdentifier: deviceData?.id?.identifier || "",
              pushToken: token.value,
              platform: device.isAndroid
                ? app_platform_type.android
                : app_platform_type.ios,
              deviceName: deviceData?.info?.name,
              osVersion: deviceData?.info?.osVersion,
            },
          },
        });
      },
      pushNotificationActionPerformed: async (action) => {
        const notificationData: FCMNotificationData | undefined =
          action?.notification?.data;

        if (!notificationData) {
          return;
        }

        if (
          notificationData.action === notification_event_type.push_notification
        ) {
          await invalidateNotificationsQuery();

          notificationsModalControl.setIsOpen(true);

          return;
        } else if (
          notificationData.action ===
          notification_event_type.message_notification
        ) {
          const chatDetailsRouteUrl = chatDetailsRouteConfig?.url;
          const messageNotificationMetadata:
            | MessageNotificationMetadata
            | undefined = notificationData?.messageNotificationMetadata
            ? JSON.parse(notificationData.messageNotificationMetadata)
            : undefined;
          const chatId = messageNotificationMetadata?.chatId;

          if (chatId) {
            await invalidateChatsQuery(chatId);
          }

          if (chatDetailsRouteUrl && lodashIsNumber(chatId)) {
            router.removeFromState("profile");
            router.push(chatDetailsRouteUrl, { chatId });
          }

          return;
        }
      },
    },
  });

  const websocketEndpoint = websocketEndpointBaseUrl.includes("://")
    ? websocketEndpointBaseUrl
    : `wss://${websocketEndpointBaseUrl}`;

  const { lastMessage } = useWebSocket<
    WebsocketNotificationEvent,
    WebsocketNotificationEvent
  >({
    url: `${websocketEndpoint}/?societyId=${session?.societyId}&deviceIdentifier=${deviceData?.id?.identifier}`,
    disabled: socketDisabled,
  });

  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    if (isNotificationMessage(lastMessage)) {
      handlePushNotificationMessage(lastMessage);
    } else if (isChatMessage(lastMessage)) {
      handleMessageNotificationMessage(lastMessage);
    }
  }, [lastMessage]);

  const notificationsContextValue: NotificationsContextType = {
    disabled,
    pushDisabled,
    socketDisabled,
    notificationsDisabled,
    chatMessagesDisabled: disabled,
    notificationsModalControl,
    unreadNotificationsCount: unreadNotificationsQuery?.data?.unreadCount,
    lastMessage,
  };

  return (
    <NotificationsContext.Provider value={notificationsContextValue}>
      {children}
    </NotificationsContext.Provider>
  );
};
